import { z } from '../utils/zod.js';
import { dataResponse } from '../utils/response-templates.js';
import { USAGE_METERS } from '../pricing-catalogue.js';
export const ADMIN_ANALYTICS_MAX_SPAN_DAYS = 400;
export const ADMIN_ANALYTICS_GRANULARITIES = ['day', 'week', 'month'];
export const adminAnalyticsGranularitySchema = z.enum(ADMIN_ANALYTICS_GRANULARITIES);
export const ADMIN_ANALYTICS_ENDPOINTS = [
    'overview',
    'signings',
    'signatures',
    'safe-transfers',
    'content-credentials',
    'conversions',
    'accounts',
    'usage',
    'verifications',
];
// Static series keys per endpoint. Keys starting with a prefix from ADMIN_ANALYTICS_DYNAMIC_PREFIXES are added
// for every value found in the data (e.g. provider_eideasy).
export const ADMIN_ANALYTICS_SERIES_KEYS = {
    overview: [],
    signings: ['created', 'completed', 'cancelled', 'expired', 'created_qualified', 'created_advanced'],
    signatures: ['qualified', 'advanced'],
    'safe-transfers': [
        'transfers_draft',
        'transfers_active',
        'transfers_expired',
        'transfers_revoked',
        'transfers_consumed',
        'recipients_pending',
        'recipients_link_opened',
        'recipients_kyc_in_progress',
        'recipients_kyc_passed',
        'recipients_kyc_failed',
        'recipients_code_locked',
        'recipients_downloaded',
        'recipients_expired',
        'recipients_revoked',
    ],
    'content-credentials': ['created'],
    conversions: [
        'pdfa',
        'pdfa_pending',
        'pdfa_converting',
        'pdfa_ready',
        'pdfa_failed',
        'docx',
        'docx_pending',
        'docx_converting',
        'docx_ready',
        'docx_failed',
    ],
    accounts: ['users_created', 'users_email_verified', 'organizations_created'],
    usage: USAGE_METERS,
    verifications: ['completed'],
};
// Keys that only appear in totals (and previous, where a previous window makes sense).
export const ADMIN_ANALYTICS_TOTAL_ONLY_KEYS = {
    overview: [
        'signings_created',
        'signings_completed',
        'signatures',
        'safe_transfers',
        'users_created',
        'organizations_created',
        'active_organizations',
    ],
    accounts: ['plan_free', 'plan_io', 'plan_enterprise'],
};
export const ADMIN_ANALYTICS_DYNAMIC_PREFIXES = {
    signatures: ['provider_'],
    verifications: ['provider_', 'verified_users_'],
};
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const isCalendarDate = (value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
};
export const adminAnalyticsDateSchema = z
    .string()
    .regex(ISO_DATE, 'Expected YYYY-MM-DD')
    .refine(isCalendarDate, 'Not a calendar date');
// Both ends are whole days and included in the window.
export const adminAnalyticsSpanDays = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;
export const adminAnalyticsQuerySchema = z
    .object({
    from: adminAnalyticsDateSchema.openapi({ description: 'First day, inclusive (UTC)' }),
    to: adminAnalyticsDateSchema.openapi({ description: 'Last day, inclusive (UTC)' }),
    granularity: adminAnalyticsGranularitySchema.default('day'),
    organizationId: z.string().uuid().optional(),
})
    .refine((query) => query.from <= query.to, { message: 'from must not be after to', path: ['to'] })
    .refine((query) => adminAnalyticsSpanDays(query.from, query.to) <= ADMIN_ANALYTICS_MAX_SPAN_DAYS, {
    message: `The window may span at most ${ADMIN_ANALYTICS_MAX_SPAN_DAYS} days`,
    path: ['to'],
});
export const adminAnalyticsPointSchema = z.object({
    bucket: z.string().regex(ISO_DATE).openapi({ description: 'Start day of the bucket' }),
    value: z.number(),
});
export const adminAnalyticsSeriesSchema = z.object({
    key: z.string(),
    label: z.string(),
    points: z.array(adminAnalyticsPointSchema),
});
export const adminAnalyticsResultSchema = z.object({
    series: z.array(adminAnalyticsSeriesSchema),
    totals: z.record(z.string(), z.number()),
    previous: z
        .record(z.string(), z.number())
        .openapi({ description: 'Same-length window immediately before from; snapshot keys are left out' }),
});
export const adminAnalyticsResponseSchema = dataResponse(adminAnalyticsResultSchema);
